<template>
  <div class='map_container'>
    <FocusMenu @focusMenuChanged="toggleBurger" ref='focusmenu'>

      <template #header>
        <div class='search_wrapper clearfix'>
          <b-form-input :disabled="!visibleMarkers" v-model="fieldNameSearch" @input="doTextSearch()"
            class="form-control-flush fields_search" placeholder="Search...">
          </b-form-input>
          <div v-show="fieldNameSearch" class='fields_search_clear_btn' @click="fieldNameSearch = ''; filterFields();">
          </div>
        </div>

        <b-form-group class='type_filter_menu'>
          <b-form-checkbox-group v-model="nodeFilterType" :options="nodeFilterTypes" size="sm" style="width:100%" buttons
            button-variant="primary" @change="filterNodeType" v-on:click.stop.prevent="() => { }">
          </b-form-checkbox-group>
        </b-form-group>
      </template>
      <b-button style="margin-left: 8px !important" v-b-modal.modal-4 size="sm" variant="primary" class="btn"
        v-b-tooltip.hover.top title="Read More">
        <b-icon icon="question-circle-fill" aria-label="Help"></b-icon>
      </b-button>
      <b-modal id="modal-4" title="Map">
        <p>The Map screen allows you to visually manage all of your installed nodes.
          Features include:</p>
        <h3>Visual Text Search</h3>
        <p>Simply type in the search query (Field Names / Node Addresses) and see
          the results filter through (as well as visually focus).</p>
        <h3>Filtering by Node Type</h3>
        <p>Clicking on a node type button will only display nodes of that type and
          hide the others.</p>
        <h3>Detail display of each node</h3>
        <p>Clicking on a Node map marker will bring up a popup with various
          information related to the node type.</p>
        <h3>Detail filtering</h3>
        <p>Further filter buttons located on the top right part of the map allow you
          to filter the details of each node's detail popup display.</p>
        <h3>Map Legends</h3>
        <p>A map legend dialog can be shown by clicking on the Question Mark Button
          on the lower right corner of the map.</p>
      </b-modal>

      <template v-for="(marker, index) in markers">
        <div :key="nodeFilterType.join('') + infoFilterType.join('') + index" v-if="marker.visible" class='field_record'
          :class="marker.node_address == selectedNode ? 'field_record_selected' : ''" @click="focusMarker(marker)">
          <div class='field_name'>{{ marker.field_name }}</div>
          <div class='node_address'>{{ marker.node_address }}</div>
          <div class='last_reading_date' :style="'color:' + calcLastReadingColor(marker)">{{ marker.date_time }}</div>
          <div v-if="marker.node_type == 'sm' && marker.status" class='field_sm_status'>Status: {{ marker.status }}%</div>
          <div class='indicators'>
            <SMStatusGraph v-if="marker.node_type == 'sm' && marker.status" :full="calcBufferFull(marker.full)"
              :status="calcMarkerStatus(marker.status, marker.refill, marker.full)"
              :refill="calcBufferRefill(marker.refill)" :size="'35%'"
              :tooltip="'F: ' + marker.full + '%, S: ' + marker.status + '%, R: ' + marker.refill + '%'" :label="'S.M'"
              @clicked="goToGraph(marker)" />
            <div v-else style="width:35%"></div>
            <BVIndicator :level="marker.battLevel" :size="'35%'" :charging="marker.charging"
              :tooltip="'Level: ' + marker.battLevel + '%'" />
          </div>
        </div>
      </template>
    </FocusMenu>

    <MglMap :repaint="true" :mapboxGl="mapboxObj" :accessToken="$store.state.mapBoxAccessToken" :mapStyle.sync="mapStyle"
      :attributionControl="false" :center="startPos" logoPosition="bottom-right" @load="mapLoaded" @zoom="scaleMarkers"
      @zoomend="filterFields" @dragend="filterFields">

      <template v-for="marker in markers">

        <MglMarker @click="toggleMarkerPopup(marker)" v-if="marker.visible" :key="marker.node_address"
          :ref="marker.node_address" :coordinates="marker.position" anchor="top">
          <div class="graph-popup">
            <template>
              <div class="content">
                <base-header class="pb-6" type="soilmoisture">
                  <b-row align-v="center" class="py-4">
                    <b-col>
                      <h6 class="h2 text-white d-inline-block mb-0 mr-5">Soil Moisture - {{ field_name }} ( {{
                        current_node }} ) -
                        Graph
                      </h6>
                      <div v-show='loading' class='mab_spinner light right'></div>
                    </b-col>
                  </b-row>
                </base-header>

                <div class='container-fluid mt--6'>
                  <div class='row'>
                    <div class='col-md-12'>
                      <card body-classes="px-0 py-0">
                        <template slot="header">
                          <div class='row'>
                            <div class='col-md-12'>
                              <base-button :disabled="!node_id || !userCan('Export', 'Soil Moisture', node_id, 'O')"
                                @click.native="exportGraphCSV()" class="btn" size="sm" type="primary"
                                v-b-tooltip.hover.top title="Export device data to a CSV file." icon>
                                Export CSV
                              </base-button>

                              <base-button :disabled="!node_id || !userCan('Edit', 'Soil Moisture', node_id, 'O')"
                                @click.native="goToManage()" class="btn" size="sm" type="primary" v-b-tooltip.hover.top
                                title="Navigate to the soil moisture node's management screen" icon>
                                Manage SM
                              </base-button>

                              <b-button v-b-modal.modal-2 size="sm" variant="primary" class="btn" v-b-tooltip.hover.top
                                title="Read More">
                                <b-icon icon="question-circle-fill" aria-label="Help"></b-icon>
                              </b-button>
                              <b-modal id="modal-2" title="Soil Moisture Graph">
                                <h3>Graph Types</h3>
                                <div class="accordion" role="tablist">
                                  <b-card no-body class="mb-1">
                                    <b-card-header header-tag="header" class="p-1" role="tab">
                                      <b-button block v-b-toggle.accordion-2 variant="text-left"
                                        class="light text-left acc-btn">Separate Levels</b-button>
                                    </b-card-header>
                                    <b-collapse id="accordion-2" accordion="my-accordion" role="tabpanel">
                                      <b-card-body>
                                        <b-card-text>
                                          <p>Soil Moisture Separate Levels with average.</p>
                                          <p>This graph type has a Zoom mode and Drag mode. If you
                                            wish to zoom into the graph just click, hold and move over the area you want
                                            to zoom
                                            into.
                                          </p>
                                          <p>Drag mode enables the user to drag the series up or down. Simply click on the
                                            toggle
                                            button below the graph to enable it.</p>
                                          <p>Click on Reset to restore the graph to the original view.</p>
                                        </b-card-text>
                                      </b-card-body>
                                    </b-collapse>
                                  </b-card>
                                  <b-card no-body class="mb-1">
                                    <b-card-header header-tag="header" class="p-1" role="tab">
                                      <b-button block v-b-toggle.accordion-3 variant="text-left"
                                        class="light text-left acc-btn">Stacked Graph</b-button>
                                    </b-card-header>
                                    <b-collapse id="accordion-3" accordion="my-accordion" role="tabpanel">
                                      <b-card-body>
                                        <b-card-text>Soil Moisture Separate Levels.</b-card-text>
                                      </b-card-body>
                                    </b-collapse>
                                  </b-card>
                                  <b-card no-body class="mb-1">
                                    <b-card-header header-tag="header" class="p-1" role="tab">
                                      <b-button block v-b-toggle.accordion-4 variant="text-left"
                                        class="light text-left acc-btn">Sum</b-button>
                                    </b-card-header>
                                    <b-collapse id="accordion-4" accordion="my-accordion" role="tabpanel">
                                      <b-card-body>
                                        <b-card-text>Sum of the levels.</b-card-text>
                                      </b-card-body>
                                    </b-collapse>
                                  </b-card>
                                  <b-card no-body class="mb-1">
                                    <b-card-header header-tag="header" class="p-1" role="tab">
                                      <b-button block v-b-toggle.accordion-5 variant="text-left"
                                        class="light text-left acc-btn">Average</b-button>
                                    </b-card-header>
                                    <b-collapse id="accordion-5" accordion="my-accordion" role="tabpanel">
                                      <b-card-body>
                                        <b-card-text>Soil Moisture Average.</b-card-text>
                                      </b-card-body>
                                    </b-collapse>
                                  </b-card>
                                  <b-card no-body class="mb-1">
                                    <b-card-header header-tag="header" class="p-1" role="tab">
                                      <b-button block v-b-toggle.accordion-6 variant="text-left"
                                        class="light text-left acc-btn">Temperature</b-button>
                                    </b-card-header>
                                    <b-collapse id="accordion-6" accordion="my-accordion" role="tabpanel">
                                      <b-card-body>
                                        <b-card-text>Soil Temperature Average</b-card-text>
                                      </b-card-body>
                                    </b-collapse>
                                  </b-card>
                                  <b-card no-body class="mb-1">
                                    <b-card-header header-tag="header" class="p-1" role="tab">
                                      <b-button block v-b-toggle.accordion-7 variant="text-left"
                                        class="light text-left acc-btn">Technical</b-button>
                                    </b-card-header>
                                    <b-collapse id="accordion-7" accordion="my-accordion" role="tabpanel">
                                      <b-card-body>
                                        <b-card-text>Battery Voltage and Ambient Tempeatures.</b-card-text>
                                      </b-card-body>
                                    </b-collapse>
                                  </b-card>
                                </div>
                                <div>
                                  <h3>Range</h3>
                                  <p>Select the date range. They consist of:</p>
                                  <p>Last 24 hours</p>
                                  <p>Last Week</p>
                                  <p>Last Two Weeks</p>
                                  <p>Last Month</p>
                                  <p>Last Year</p>
                                  <p>Custom</p>
                                  <h3>Start Date</h3>
                                  <p>If you select Custom in the date range dropdown this dropdown will appear. You will
                                    be required
                                    to select a start date.</p>
                                </div>
                                <div>
                                  <h3>Print or Export</h3>
                                  <p>A map can be printed in PDF or exported as PNG, JPG, PDF or SVG.</p>
                                </div>
                                <div>
                                  <h3>Full and Refill lines</h3>
                                  <p>Full (blue): The Full value that applies to the field</p>
                                  <p>Refill (red): The Refill value that applies to the field</p>
                                  <h3>Series</h3>
                                  <p>The series values appear beneath the name of the field. The series display the time
                                    and the
                                    calculated value of the reading.</p>
                                </div>
                              </b-modal>
                            </div>
                          </div>
                        </template>
                        <div class="card-body">
                          <b-row>
                            <!-- <b-col md="6"> -->
                            <b-col md="4" v-if="related_nodes.length > 1">
                              <base-input label="Current Node" v-b-tooltip.hover.top
                                title="The Node Address to which this data record applies to">
                                <el-select v-model="current_node" :disabled="loading">
                                  <template v-for="(node, index) in related_nodes">
                                    <el-option :key="index" :label="node" :value="node"
                                      :selected='$route.params.node_address'></el-option>
                                  </template>
                                </el-select>
                              </base-input>
                            </b-col>

                            <b-col md="4">
                              <base-input label="Graph Type">
                                <el-select v-model="graph_type" :disabled="loading" v-b-tooltip.hover.top
                                  title="The graph type used when viewing a probe's graph">
                                  <el-option label="Separate Levels" value='sm' selected></el-option>
                                  <el-option label="Stacked Graph" value='stack'></el-option>
                                  <el-option label="Sum" value='sum'></el-option>
                                  <el-option label="Average" value='ave'></el-option>
                                  <el-option label="Temperature" value='temp'></el-option>
                                  <el-option label="Technical" value='tech'></el-option>
                                </el-select>
                              </base-input>
                            </b-col>

                            <div class="w-100"></div>

                            <b-col md="4">
                              <base-input label="Range">
                                <el-select v-model="sub_days" :disabled="loading" v-b-tooltip.hover.top
                                  title="Choose the time range of data to view.">
                                  <el-option label="Last 24 Hours" value='1'></el-option>
                                  <el-option label="Last Week" value='7'></el-option>
                                  <el-option label="Last Two Weeks" value='14'></el-option>
                                  <el-option label="Last Month" value='31'></el-option>
                                  <el-option label="Last Year" value='365'></el-option>
                                  <el-option label="Custom" value='custom'></el-option>
                                </el-select>
                              </base-input>
                            </b-col>

                            <b-col md="4">
                              <base-input v-show="sub_days == 'custom'" label="Start Date">
                                <flat-picker slot-scope="{focus, blur}" @on-open="focus" @on-close="blur"
                                  v-model="graph_start_date" :config="{ maxDate: date_now }" placeholder="Custom Date"
                                  class="form-control datepicker" v-b-tooltip.hover.top
                                  title="A custom start date allows a custom range of data to be viewed.">
                                </flat-picker>
                              </base-input>
                            </b-col>
                            <!-- </b-col> -->

                          </b-row>
                          <b-row>
                            <b-col class='text-center'>
                              <b-form-checkbox v-if="graph_type == 'sm' || graph_type == 'stack'"
                                @change="toggleSeriesDragDrop" v-model="zoomOrDrag" name="check-button" size="lg"
                                switch>Mode: {{ zoomOrDrag ? "Drag" : "Zoom"
                                }}</b-form-checkbox>
                            </b-col>
                          </b-row>
                        </div>
                      </card>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
          <div class='field_marker' slot="marker">
            <div class='marker'
              :style="'color:' + calcMarkerIconColor(marker, marker.status, marker.node_type) + '; transform:scale(' + markerScale + '); transform-origin:top center;' + calcMarkerBorderColor(marker)">
              <template v-if="marker.node_type == 'well'">
                <svg stroke-linejoin="bevel" fill-rule="evenodd" xmlns="http://www.w3.org/2000/svg" overflow="visible"
                  width="100%" height="100%" viewBox="0 0 165 187.5">
                  <path stroke-width="0"
                    d="M.001 187.5v-7.499h7.484v-52.499h7.482v-7.501h7.482V67.5H7.484v-7.499h14.965V39.002L7.484 45.001H0V30h7.484v.001l74.829-30 74.83 30h7.483v15.001h-7.484l-.001-.001-14.964-5.998.001 20.998h7.857v-.002h7.482V75H165v7.499h-7.483v.001h-7.482v-15h-7.857v52.501h7.482v7.501h7.483v52.499h7.482v7.499H.001zm119.728-59.998v-7.501h7.482V67.5h-14.967v7.501h-26.04v15h18.558l-7.484 30h-29.93l-7.483-30h18.857v-15h-26.34V67.5H37.416v52.501h7.482v7.501h74.831zM52.382 60.001v-7.499h59.862v7.499h14.967V33.002L82.313 15.001l-44.897 18v27h14.966z"
                    fill="currentColor" stroke="white" font-family="Times New Roman" font-size="16" />
                </svg>
              </template>
              <template v-else-if="marker.node_type == 'meter'">
                <svg stroke-linejoin="bevel" fill-rule="evenodd" xmlns="http://www.w3.org/2000/svg" overflow="visible"
                  width="100%" height="100%" viewBox="0 0 225 150">
                  <path stroke-width="0"
                    d="M43.746 105.001H22.499V120H0V29.999h22.499V45h21.246C55.326 18.519 81.762 0 112.498 0c30.735 0 57.171 18.519 68.752 45h21.249V29.999h22.499V120h-22.499v-14.999h-21.25C169.669 131.482 143.233 150 112.498 150c-30.736 0-57.172-18.518-68.752-44.999zM179.998 75c0-37.26-30.24-67.5-67.5-67.5-37.26 0-67.5 30.24-67.5 67.5 0 37.26 30.24 67.5 67.5 67.5 37.26 0 67.5-30.24 67.5-67.5zm-127.5 0c0-33.12 26.88-60 60-60 33.12 0 60 26.88 60 60 0 33.12-26.88 60-60 60-33.12 0-60-26.88-60-60zm37.5 22.5V82.501H74.999V97.5h14.999zm30-.001V82.5h-14.999v14.999h14.999zm30 0V82.5h-14.999v14.999h14.999zm.001-30V52.5H74.998v14.999h75.001z"
                    fill="currentColor" stroke="white" font-family="Times New Roman" font-size="16" />
                </svg>
              </template>
              <template v-else-if="marker.node_type == 'sm'">
                <svg stroke-width=".501" stroke-linejoin="bevel" fill-rule="evenodd" xmlns="http://www.w3.org/2000/svg"
                  overflow="visible" width="100%" height="100%" viewBox="0 0 90 187.5">
                  <g stroke-linecap="round" stroke="none" fill="none" font-size="16">
                    <path
                      d="m37.499 187.5-15-15V45l-7.5-7.5V7.499H0V0h74.999v7.499H59.998v7.502h22.5v-7.5h7.501V37.5h-7.501V30h-22.5l.001 7.5-7.5 7.5v127.5l-15 15Z"
                      fill="currentColor" />
                    <path
                      d="m7.177 124.653 15.222-1.48c.916 5.109 2.776 8.861 5.576 11.258 2.801 2.396 6.579 3.595 11.338 3.595 5.038 0 8.835-1.066 11.39-3.198 2.554-2.132 3.83-4.625 3.832-7.48-.002-1.831-.538-3.39-1.613-4.677-1.074-1.285-2.95-2.404-5.629-3.355-1.831-.635-6.008-1.762-12.527-3.383-8.386-2.079-14.27-4.635-17.653-7.665-4.757-4.263-7.135-9.461-7.135-15.592 0-3.946 1.119-7.637 3.356-11.072 2.237-3.437 5.462-6.053 9.673-7.85 4.211-1.796 9.294-2.695 15.247-2.695 9.726 0 17.046 2.132 21.962 6.395 4.915 4.264 7.496 9.954 7.743 17.072l-15.645.687c-.669-3.981-2.104-6.844-4.307-8.588-2.202-1.744-5.506-2.617-9.911-2.617-4.544 0-8.104.935-10.677 2.802-1.657 1.198-2.483 2.801-2.483 4.81 0 1.832.774 3.4 2.325 4.704 1.973 1.655 6.766 3.381 14.376 5.179 7.612 1.797 13.241 3.656 16.888 5.576 3.647 1.922 6.501 4.547 8.562 7.875 2.061 3.33 3.092 7.444 3.092 12.342 0 4.439-1.232 8.598-3.699 12.474-2.466 3.877-5.956 6.757-10.466 8.641-4.51 1.885-10.13 2.828-16.861 2.828-9.796 0-17.319-2.264-22.569-6.792-5.25-4.527-8.386-11.126-9.407-19.794Z"
                      fill-rule="nonzero" stroke-linejoin="round" stroke-width=".375" fill="#fff"
                      stroke-miterlimit="79.84" />
                  </g>
                </svg>

              </template>
              <template v-else-if="marker.node_type == 'nut'">
                <svg stroke-width=".501" stroke-linejoin="bevel" fill-rule="evenodd" xmlns="http://www.w3.org/2000/svg"
                  overflow="visible" width="100%" height="100%" viewBox="0 0 90 187.5">
                  <g stroke-linecap="round" stroke="none" fill="none" font-size="16">
                    <path
                      d="m37.499 187.5-15-15V45l-7.5-7.5V7.499H0V0h74.999v7.499H59.998v7.502h22.5v-7.5h7.501V37.5h-7.501V30h-22.5l.001 7.5-7.5 7.5v127.5l-15 15Z"
                      fill="currentColor" />
                    <path
                      d="M9.001 147.09V75.155h14.132l29.441 48.039V75.155h13.493v71.935H51.494l-28.999-46.909v46.909H9.001Z"
                      fill-rule="nonzero" stroke-linejoin="round" stroke-width=".375" fill="#fff"
                      stroke-miterlimit="79.84" />
                  </g>
                </svg>
              </template>
            </div>
          </div>

          <MglPopup :closeButton="true" anchor="bottom" maxWidth="auto" :closeOnClick="false" @close="popupClosed">
            <div class='marker_info_wrap'>
              <div class="the_graph">
                <highcharts class="hc" :options="chartOptions" :update-args="chartUpdateArgs" ref="hchart">
                </highcharts>
              </div>
              <div v-if="marker.node_type == 'well'" class='pump_control'>
                <div class='field_name'>{{ marker.field_name }}</div>
                <div class='node_address'>{{ marker.node_address }}</div>
                <div class='last_reading_date'>{{ marker.date_time }}</div>
                <div class='pump_control_inner'>
                  <b-form-checkbox :disabled="!userCan('Toggle', 'Well Controls', marker.id, 'O')"
                    v-model="marker.pump_status" switch size="lg" @change="togglePump(marker)">
                    {{ marker.pump_status ? 'Pump: On' : 'Pump: Off' }}
                  </b-form-checkbox>
                </div>
              </div>

              <div v-if="marker.node_type == 'meter'" class='meter_control'>
                <div class='field_name'>{{ marker.field_name }}</div>
                <div class='node_address'>{{ marker.node_address }}</div>
                <div class='last_reading_date'>{{ marker.date_time }}</div>
                <MeterReading :reading="marker.last_reading" :suffix="formatUOM(marker.measurement_type)"></MeterReading>
              </div>

              <div v-if="marker.node_type == 'sm'" class='sm_control'>
                <template v-for="(inner, idx) in markers">
                  <div v-if="inner.base_node_address == marker.base_node_address &&
                    inner.node_type == 'sm' &&
                    inner.date_time != '1970-01-01 00:00:00' &&
                    (Math.abs(inner.latt - marker.latt) < 0.00001) &&
                    (Math.abs(inner.lng - marker.lng) < 0.00001)
                    " :key="idx" class='sm_control_item'
                    :class="inner.node_address == selectedNode ? 'selected_node' : ''">
                    <div class='field_name'><a style="border-bottom:2px solid red;color:white;" :href="marker.link">{{
                      inner.field_name }}</a></div>
                    <div class='node_address'>{{ inner.node_address }}</div>
                    <div class='last_reading_date'>{{ inner.date_time }}</div>
                    <div class='marker_info_horz'>
                      <SMStatusIndicator v-if="infoFilterType.includes('sm_status') && inner.status" :size="'5em'"
                        :full="inner.full" :status="inner.status" :refill="inner.refill" :accordion="true"
                        :showNumbers="true">
                      </SMStatusIndicator>

                      <SMLevelsIndicator v-if="infoFilterType.includes('sm_levels') && inner.sm1" :size="'5em'"
                        :tooltip="'S.M Levels'" :sm1="inner.sm1" :sm2="inner.sm2" :sm3="inner.sm3" :sm4="inner.sm4"
                        :sm5="inner.sm5" :sm6="inner.sm6" :sm7="inner.sm7" :sm8="inner.sm8" :sm9="inner.sm9"
                        :sm10="inner.sm10" :sm11="inner.sm11" :sm12="inner.sm12" :sm13="inner.sm13" :sm14="inner.sm14"
                        :sm15="inner.sm15">
                      </SMLevelsIndicator>

                      <SoilTempIndicator v-if="infoFilterType.includes('soil_temp') && inner.t1" :size="'5em'"
                        :tooltip="'Soil Temperatures'" :t1="inner.t1" :t2="inner.t2" :t3="inner.t3" :t4="inner.t4"
                        :t5="inner.t5" :t6="inner.t6" :t7="inner.t7" :t8="inner.t8" :t9="inner.t9" :t10="inner.t10"
                        :t11="inner.t11" :t12="inner.t12" :t13="inner.t13" :t14="inner.t14" :t15="inner.t15"
                        :suffix="$store.getters.getUOMEntity" :uom="$store.getters.getUOM">
                      </SoilTempIndicator>
                    </div>
                  </div>
                </template>
              </div>

              <div v-if="marker.node_type == 'nut'" class='sm_control'>
                <template v-for="(inner, idx) in markers">
                  <div v-if="inner.base_node_address == marker.base_node_address &&
                    inner.node_type == 'nut' &&
                    inner.date_time != '1970-01-01 00:00:00' &&
                    (Math.abs(inner.latt - marker.latt) < 0.0001) &&
                    (Math.abs(inner.lng - marker.lng) < 0.0001)
                    " :key="idx" class='sm_control_item'
                    :class="inner.node_address == selectedNode ? 'selected_node' : ''">
                    <div class='field_name'><a style="border-bottom:2px solid red;color:white;" :href="marker.nut_link">{{
                      inner.field_name }}</a></div>
                    <div class='node_address'>{{ inner.node_address }}</div>
                    <div class='last_reading_date'>{{ inner.date_time }}</div>
                    <div class='marker_info_horz'>
                    </div>
                  </div>
                </template>
              </div>

            </div>
          </MglPopup>
        </MglMarker>
      </template>
    </MglMap>

    <b-form-group class='info_filter_menu'>
      <b-form-checkbox-group v-model="infoFilterType" :options="infoFilterTypes" stacked buttons button-variant="primary"
        size="sm" @change="filterNodeType" v-on:click.stop.prevent="() => { }">
      </b-form-checkbox-group>
    </b-form-group>

    <base-button icon type="primary" class='legend_btn' size="sm" @click='toggleLegendModal()'><span
        class='cirle'>?</span></base-button>

    <b-modal v-model="showLegendModal" dialog-class='legend_modal' centered hide-backdrop no-close-on-esc
      no-close-on-backdrop>

      <template #modal-header="{ close }">
        <h6 slot="header" class="modal-title" id="modal-title-default">Legend</h6>
      </template>

      <template #default="{ hide }">
        <div class='legend_box'>
          <b-row>
            <b-col md>
              <h3>Soil Moisture Probes</h3>
              <div class='line'><span class='licon blue' v-html="renderSvgIcon('sm')"></span> 75%+ Status </div>
              <div class='line'><span class='licon green' v-html="renderSvgIcon('sm')"></span> 50-75% Status </div>
              <div class='line'><span class='licon orange' v-html="renderSvgIcon('sm')"></span> 25-50% Status </div>
              <div class='line'><span class='licon red' v-html="renderSvgIcon('sm')"></span>
                <=25% Status </div>
                  <div class='line'><span class='licon yellow' v-html="renderSvgIcon('sm')"></span> Status older than 3
                    days </div>

            </b-col>
            <!-- <b-col md>
              <h3>Nutrient Probes</h3>
              <div class='line'><span class='licon blue' v-html="renderSvgIcon('nut')"></span> Coming Soon </div>
            </b-col> -->
          </b-row>
          <b-row>
            <b-col md>
              <h3>Wells</h3>
              <div class='line'><span class='licon green' v-html="renderSvgIcon('well')"></span> Pump Running </div>
              <div class='line'><span class='licon red' v-html="renderSvgIcon('well')"></span> Pump Idle </div>
            </b-col>
          </b-row>
          <b-row>
            <b-col md>
              <h3>Meters</h3>
              <div class='line'><span class='licon blue' v-html="renderSvgIcon('meter')"></span> Water Meter </div>
            </b-col>
          </b-row>
        </div>
      </template>

      <template #modal-footer="{ ok, cancel, hide }">
        <base-button type="link" class="ml-auto" variant="outline-primary"
          @click="toggleLegendModal()">Close</base-button>
      </template>
    </b-modal>

    <div v-show='loading' class='mab_spinner light center large'></div>
  </div>
</template>
<script>
import MapboxGL from "mapbox-gl";
import MABMapStyle from '../../assets/mapstyles/mab_mapstyle.min.js';
import { MglMap, MglMarker, MglPopup } from "v-mapbox";
import FocusMenu from '../../custom_components/FocusMenu';
import BVIndicator from '../../custom_components/BVIndicator';
import SMStatusGraph from '../../custom_components/SMStatusGraph';
import SMStatusIndicator from '../../custom_components/SMStatusIndicator';
import SMLevelsIndicator from '../../custom_components/SMLevelsIndicator';
import SoilTempIndicator from '../../custom_components/SoilTempIndicator';
import MeterReading from '../../custom_components/MeterReading';
import EasingFunctions from "../../easingFunctions";
import mab_utils from '../../util/mab-utils';
import Highcharts from 'highcharts';
import swal from 'sweetalert2';

// Tweak to prevent Tooltip from disappearing (Testing working)
Highcharts.Pointer.prototype.reset = function () { return undefined; };

export default {

  mixins: [mab_utils],

  components: {
    MglMap,
    MglMarker,
    MglPopup,
    FocusMenu,
    BVIndicator,
    SMStatusGraph,
    SMStatusIndicator,
    SMLevelsIndicator,
    SoilTempIndicator,
    MeterReading
  },

  data() {
    return {
      loading: false,
      startPos: [0, 0],
      startZoom: 3,
      mapboxObj: null,
      map: null,
      markers: [],
      markerScale: 0,
      toggleVisible: 0,
      visibleMarkers: 0,
      visiblePopups: [],
      fieldNameSearch: '',
      mapActions: null,
      mapStyle: '', /* was NULL */
      fieldsData: null,
      bounds: null,
      showLegendModal: false,
      refreshTimer: null,
      bInitialQuery: true,
      selectedNode: null,

      infoFilterType: ['sm_status', 'labels'],
      infoFilterTypes: [
        { text: 'S.M. Status', value: 'sm_status' },
        { text: 'S.M. Levels', value: 'sm_levels' },
        { text: 'Soil Temp', value: 'soil_temp' },
        { text: 'Rain Gauge', value: 'rain_gauge' },
        { text: 'Wells', value: 'wells' },
        { text: 'Weather S.', value: 'weather_s' },
        { text: 'Labels', value: 'labels' },
      ],

      nodeFilterType: ['sm', 'nut', 'well', 'meter'],
      nodeFilterTypes: [
        { text: 'S.M', value: 'sm' },
        { text: 'Nutrient', value: 'nut' },
        { text: 'Well', value: 'well' },
        { text: 'Meter', value: 'meter' },
      ],

      loading: false,
      in_progress: true,

      tooltip: {
        enabled: true,
      },



      current_node: this.$route.params.node_address,

      node_id: null,
      field_name: null,
      data_grouping: false,


      graph_start_date: '',
      graph_type: '',
      sub_days: '',

      bInitialQuery: true,
      bNotified: false,

      related_nodes: [],

      labelsEnabled: true,
      visible: true,

      reversed: false,

      //data: [],

      date_now: '',
      series: [],
      chartUpdateArgs: [true, true, { duration: 1000 }],
      seriesAdjusted: false,
      zoomOrDrag: false, /* false == zoom, true == drag */
      stacking: 'normal',
      lastDate: null,
      exportFileName: '',

      initial_start_date: null,
      initial_end_date: null,

      selection_start: null,
      selection_end: null,


      yMaxBackup: 0,
      yMinBackup: 0,

      yMin: null,
      yMax: null,

      xMin: 1000,
      xMax: 0,

      lastTimestamp: null,

      full: 0,
      refill: 0,

      status: '',

      plotOptions: {
        series: {
          states: {
            inactive: {
              opacity: 1
            }
          },
        }
      },
      chartTitle: {
        text: "Loading..",
        widthAdjust: -300
      },
      plotLines: [],
      resetButton: {
        text: "Reset",
        enabled: true,
        onclick: (e) => {
          this.resetSeriesData();
        }
      }
    }
  },

  computed: {
    chartOptions() {

      return {
        credits: { enabled: false },
        chart: {
          marginTop: 160,
          type: 'line',
          zoomType: this.zoomOrDrag == false ? 'x' : false,
          panning: true,
          panKey: 'shift',
          height: 600,
          resetZoomButton: { position: { x: 0, y: -50 } },
          scrollablePlotArea: {
            minWidth: 700,
            scrollPositionX: 1
          },
        },
        exporting: {
          buttons: {
            contextButton: { enabled: false },
            exportButton: { text: 'Export', menuItems: ['downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadSVG'] },
            printButton: { text: 'Print', onclick: function () { this.print(); } },
            resetButton: this.resetButton
          },
          filename: this.exportFileName
        },
        colors: ["#7cb5ec", "#434348", "#90ed7d", "#f7a35c", "#8085e9", "#f15c80", "#e4d354", "#2b908f", "#f45b5b", "#91e8e1"],
        series: this.series,
        xAxis: {
          min: this.xMin,
          max: this.xMax,
          type: 'datetime',
          dateTimeLabelFormats: {
            millisecond: '%H:%M:%S.%L',
            second: '%H:%M:%S',
            minute: '%H:%M',
            hour: '%H:%M',
            day: '<span style="fill: #00A04C; font-weight:600">%e %b</span>',
            week: '%e %b',
            month: '%b %Y',
            year: '%Y'
          },
          tickPosition: 'inside',
          labels: {
            rotation: -45,
          },
          crosshair: true,

          events: {
            setExtremes: function (e) {
              var c = this;
              setTimeout(function () {
                if (typeof e.min == 'undefined' && typeof e.max == 'undefined') {
                  // 'zoom-reset'
                  c.chart.yAxis[0].setExtremes(c.chart.yAxis[0].min, c.chart.yAxis[0].max);
                } else {
                  // 'zoom-in'
                  c.chart.yAxis[0].setExtremes();
                }
              }, 1);
            },
            afterSetExtremes: this.afterSetExtremes
          }

        },
        yAxis: {
          tickInterval: 1,
          max: this.yMax,
          min: this.yMin,
          minPadding: 0,
          maxPadding: 0,
          title: { text: '' },
          plotLines: this.plotLines,
          // labels: {
          //   enabled: this.labelsEnabled
          // },
          visible: this.visible
        },
        legend: {
          layout: 'horizontal',
          align: 'center',
          verticalAlign: 'bottom',
          labelFormatter: function () {
            return this.name;
          },
        },
        tooltip: {
          shape: "square",
          headerShape: "callout",
          borderWidth: 0,
          shadow: false,
          useHTML: true,
          positioner: function () {
            return { x: 50, y: 0 };
          },
          split: true,
          distance: 30,
          padding: 5,
          formatter() {
            let s = "";
            s = '<span class="date_of_reading" style="color:#444;">' + Highcharts.dateFormat('%A, %b %e %Y, %H:%M:%S', new Date(this.x)) + '</span><br>';
            this.points.forEach(function (point) {

              var series_y = point.y;
              let series_name = point.series.name;

              if (series_name.includes("Battery")) {
                series_y = parseFloat(point.y).toFixed(3);
              }

              else if (series_name.includes("Temp")) {
                series_y = parseFloat(point.y).toFixed(1);
                if (series_name == "Ambient Temperatures") {
                  series_y = Math.trunc(point.y);
                }
              }

              else {
                series_y = parseFloat(point.y).toFixed(2);
              }

              s += '<span style="color:' + point.series.color + '; font-size:13px;">●</span> <span style="color:#444;font-size:13px;font-weight:bold">' + point.series.name + ': </span><span style="color:firebrick;">' + series_y + '</span> <img src="https://img.icons8.com/ios-filled/15/000000/vertical-line.png"/> ';
              if (point.series.name == 'Average' && point.point.status) {
                s += '<span style="color:orange;">●</span> Status: <strong>' + point.point.status + '%</strong><br>';
              }
            });
            return s;
          },
          shared: true,
          // valueDecimals: 2,
        },
        plotOptions: this.plotOptions,
        title: this.chartTitle,
        responsive: {
          rules: [{
            condition: {
              maxWidth: 800,
            },
            chartOptions: {
              legend: {
                enabled: true,
                layout: 'horizontal',
                align: 'center',
                verticalAlign: 'bottom',
                labelFormatter: function () {
                  return this.name;
                },
                x: 0,
                y: 0
              },
            }
          }],
        },
      }
    },

  },

  watch: {
    title: function (val) {
      this.chartOptions.title = val;
    },
    plotOptions: {
      handler: function (val) {
        if (val.length) {
          this.chartOptions.plotOptions = val;
        }
      },
      deep: true
    },
    series: {
      handler: function (val) {
        if (val.length) {
          this.chartOptions.series = val;
        }
      },
      deep: true
    },
    graph_start_date: function (val) {
      this.changeDateRange();
    },
    graph_type: function (val) {
      this.maybeReloadGraphData();
    },
    sub_days: function (val) {
      if (val == 'custom' && !this.graph_start_date) { return; }
      if (val != 'custom') { this.graph_start_date = ''; }
      // Experimental
      this.initial_start_date = null;
      this.initial_end_date = null;
      this.maybeReloadGraphData();
    },
    current_node: function (val) {
      this.bNotified = false;
      this.maybeReloadGraphData();
    }
  },

  created() {
    var today = new Date();
    var dd = String(today.getDate()).padStart(2, '0');
    var mm = String(today.getMonth() + 1).padStart(2, '0'); //January is 0!
    var yyyy = today.getFullYear();
    this.date_now = today = yyyy + '-' + mm + '-' + dd;
  },

  // #2 (created == #1)
  mounted() {
    this.mapStyle = MABMapStyle;
    this.loadGraphData();
  },

  methods: {
    loadGraphData() {

      this.loading = true;
      this.in_progress = true;

      this.$refs.hchart.chart.showLoading(this.bInitialQuery ? "Loading..." : "Refreshing...");

      return this.$axios.post("/api/Graph", {
        node_address: this.current_node,
        graph_type: this.graph_type,
        graph_start_date: this.graph_start_date,
        sub_days: this.sub_days,
        is_initial: this.bInitialQuery,
        selection_start: this.selection_start,
        selection_end: this.selection_end
      }).then(resp => {

        this.loading = false;
        this.in_progress = true;
        this.$refs.hchart.chart.hideLoading();


        if (resp.data) {
          if (resp.data.message && resp.data.message == 'no_data') {
            swal.fire({
              title: 'No data found',
              text: "This node currently has no data to plot.",
              showCancelButton: false,
              confirmButtonText: 'OK',
              buttonsStyling: false,
              customClass: {
                cancelButton: 'btn btn-outline-primary',
                confirmButton: 'btn btn-primary'
              }
            });
            this.chartTitle.text = "No data found";
            //setTimeout(() => { this.in_progress = false; }, 1);
            return;
          }

          if (!this.bNotified && resp.data.not_current && resp.data.last_date) {
            swal.fire({
              title: 'No recent data found',
              text: "Last readings up to " + resp.data.last_date + " could be displayed.",
              showCancelButton: false,
              confirmButtonText: 'OK',
              buttonsStyling: false,
              customClass: {
                cancelButton: 'btn btn-outline-primary',
                confirmButton: 'btn btn-primary'
              }
            });
            this.bNotified = true;
          }

          this.selection_start = null;
          this.selection_end = null;

          // COMMON DATA
          this.node_id = resp.data.node_id;
          this.field_name = resp.data.field_name;
          // Experimental
          if (this.bInitialQuery) {
            this.graph_start_date = resp.data.graph_start_date;
            this.graph_type = resp.data.graph_type;
            this.sub_days = resp.data.sub_days;
          }
          this.xMax = resp.data.x_max;
          this.xMin = resp.data.x_min;
          //this.yMin = resp.data.y_min;


          if (this.graph_type == 'ave') {
            this.yMax = resp.data.y_max;
            this.yMin = resp.data.y_min;
          } else {
            this.yMax = null;
            this.yMin = null;
          }

          if (this.graph_type == 'stack') {
            this.reversed = false;
          }
          // else {
          //   this.reversed = false;
          // }

          this.yMaxBackup = resp.data.y_max; // used with toggle switch
          this.yMinBackup = resp.data.y_min; // used with toggle switch

          this.full = resp.data.full;
          this.refill = resp.data.refill;

          this.lastDate = resp.data.last_date.replace(":", '-');
          this.exportFileName = resp.data.graph.field_name + '-' + this.graph_type + '-' + this.$route.params.node_address + '-' + this.lastDate;

          this.plotOptions = resp.data.graph.plotOptions;
          this.chartTitle = resp.data.graph.title;

          this.series = resp.data.graph.series.filter((series) => { if (series.data) { return series.data.length > 0; } else return false; });

          this.lastTimestamp = resp.data.x_max;

          if (this.graph_type == 'sm' || this.graph_type == 'stack') {
            if (this.zoomOrDrag) {
              this.enableSeriesDragDrop();
            } else {
              this.disableSeriesDragDrop();
            }
          } else {
            this.disableSeriesDragDrop();
          }

          // if (this.graph_type == 'stack') {
          //   console.log('stack found');

          //   if (this.zoomOrDrag) {
          //   console.log('stack found2');

          //     this.enableSeriesDragDrop();
          //   } else {
          //   console.log('stack found3');

          //     this.disableSeriesDragDrop();
          //   }

          // } else {
          //   this.disableSeriesDragDrop();
          // }

          if (this.graph_type == 'stack') {
            this.visible = false;
            this.plotOptions.series.stacking = this.stacking;
          } else {
            this.visible = true;
            this.plotOptions.series.stacking = '';
          }

          if (this.graph_type == 'ave') {
            this.showPlotLines();
            //this.formatTooltip();
            this.setupCustomTooltip();
          } else {
            this.hidePlotLines();
            this.removeCustomTooltip();
          }

          this.related_nodes = resp.data.related_nodes;
          this.$refs.hchart.chart.reflow();

        }
        setTimeout(() => { this.in_progress = false; this.bInitialQuery = false; }, 1);
        return resp;
      });
    },

    maybeReloadGraphData() {
      if (this.in_progress == true) { return; }
      this.loadGraphData();
    },

    changeDateRange() {

      if (this.in_progress == true) { return; }
      if (this.graph_start_date == '') { return; }
      this.loadGraphData();
    },

    enableSeriesDragDrop() {
      if (this.series && this.series.length) {
        // console.log("Enabling Series Drag and Drop");
        // console.log(this.series);

        if (this.graph_type == 'stack') {
          // Remove labels on yAxis when DnD is true
          this.labelsEnabled = false;

          this.chartOptions.yAxis.visible = false;
        } else {
          this.labelsEnabled = true;
        }

        // Enable shared tooltip
        this.chartOptions.tooltip = { shared: true };
        // Enable custom Reset button
        this.resetButton.enabled = true;

        this.series.forEach((series, idx) => {

          // Set custom Per Series Data
          this.series[idx].custom = {
            moved: false
          };

          this.series[idx].pointStart = Date.UTC(2004, 3, 1);
          this.series[idx].pointInterval = 3600 * 1000;

          // Enable DnD for this range of series only
          this.series[idx].dragDrop = {
            draggableX: false,
            draggableY: true,
            dragMinY: 0,
            dragMaxY: 100,
            groupBy: 'grp',
          };

          this.series[idx].dragDrop.draggableX = false;
          this.series[idx].dragDrop.draggableY = true;
          if (this.series[idx].name != 'Average') {
            this.series[idx].dragDrop.groupBy = 'grp' + (idx + 1);
          } else {
            this.series[idx].dragDrop.groupBy = 'grpavg';
          }

          // Track changes
          this.series[idx].point = {
            events: {
              dragStart: function () {
                this.series.options.custom.moved = true;
              },
            }
          };

          // Format tooltip: Hide value once series was moved
          this.series[idx].tooltip = {

            pointFormatter: function () {
              return '<span style="color:' + this.series.color + ';">●</span> ' + this.series.name + ': <b>' +
                (this.series.options.custom.moved ? '' : Highcharts.numberFormat(this.y, 2)) + '</b><br/>';
            }
          };
        });
        // Lock in Y Max/Min
        // if(this.graph_type != 'ave'){
        //   this.yMax = this.yMaxBackup;
        //   this.yMin = this.yMinBackup;
        // }

        // Lock in Y Max/Min
        if (this.graph_type != 'ave') {
          //console.log(this.$refs.hchart);
          this.yMax = this.$refs.hchart.chart.yAxis[0].max;
          this.yMin = this.$refs.hchart.chart.yAxis[0].min;
        }

        this.chartTriggerChange();
      }
    },

    disableSeriesDragDrop() {
      if (this.series && this.series.length) {
        //console.log("Disabling series DnD");

        // Disable drag
        this.series.forEach((series, idx) => {
          // if(typeof(this.series[idx].dragDrop) === "undefined"){
          this.series[idx].dragDrop = {
            draggableX: false,
            draggableY: false,
            groupBy: this.series[idx].name == 'Average' ? 'grpavg' : ('grp' + (idx + 1))
          };
          // } else {
          //   this.series[idx].dragDrop.draggableX = false;
          //   this.series[idx].dragDrop.draggableY = false;
          // }
          // this.series[idx].dragDrop.draggableX = false;
          // this.series[idx].dragDrop.draggableY = false;

          this.series[idx].pointStart = Date.UTC(2004, 3, 1);
          this.series[idx].pointInterval = 3600 * 1000;
        });

        // Enable labels on yAxis when DnD is false
        this.labelsEnabled = true;

        // Disable shared tooltip
        this.chartOptions.tooltip = { shared: false };
        // Disable custom Reset button
        this.resetButton.enabled = false;

        // Disable Y Max/Min
        if (this.graph_type != 'ave') {
          this.yMax = null;
          this.yMin = null;
        }

        this.chartTriggerChange();
      }
    },

    toggleSeriesDragDrop() {
      // Zoom
      if (this.zoomOrDrag) {
        this.enableSeriesDragDrop();
        // Drag
      } else {
        this.disableSeriesDragDrop();
      }
    },

    // toggleStacking()
    // {
    //   console.log('Toggle Stacking');
    //   this.plotOptions.series.stacking = this.stacking;
    //   this.chartTriggerChange();
    // },

    showPlotLines() {
      this.plotLines = [
        { /* Full Line */
          value: this.full,
          color: '#00F',
          width: 2,
          label: {
            x: 0,
            align: 'right',
            formatter: () => { return 'Full: ' + this.full; }
          }
        },
        { /* Refill Line */
          value: this.refill,
          color: '#D26761',
          width: 2,
          label: {
            x: 0,
            align: 'right',
            formatter: () => { return 'Refill: ' + this.refill; }
          }
        }
      ];
    },

    hidePlotLines() {
      this.plotLines = [];
    },

    // Hack to trigger chart update via faux change
    chartTriggerChange() {
      // console.log('hi');
      this.series.push({ type: 'line', name: 'Faux', data: [] });
      this.series.pop();
    },

    exportGraphCSV() {

      this.zoomOrDrag = false;

      // accessing internal series data from the outside
      let series = this.$refs.hchart.chart.series;
      try {
        for (var i = 0; i < series.length; i++) {
          if (typeof series[i].options.custom.moved !== "undefined") {
            if (series[i].options.custom.moved == true) {
              this.seriesAdjusted = true;
              break;
            }
          }
        }
      } catch (err) { }

      if (this.seriesAdjusted) {
        this.resetSeriesData().then((resp) => {
          this.$refs.hchart.chart.downloadCSV();
        });
      } else {
        this.$refs.hchart.chart.downloadCSV();
      }
    },

    resetSeriesData() {
      this.seriesAdjusted = false;
      return this.loadGraphData();
    },

    formatTooltip() {
      // let s = "";
      // s = '<span class="date_of_reading" style="color:#444;">' + Highcharts.dateFormat('%A, %b %e %Y, %H:%M:%S', new Date(this.x)) + '</span><br>';
      // this.series.forEach(function (point) {
      //   console.log(point);
      //   s += '<span style="color:' + point.color + '; font-size:13px;">●</span> <span style="color:#444;font-size:13px;font-weight:bold">' + point.name + ': </span><span style="color:firebrick;">' + point.y + '</span> <img src="https://img.icons8.com/ios-filled/15/000000/vertical-line.png"/> ';
      //   if (point.name == 'Average' && point.status) {
      //     s += '<span style="color:black;">●</span> Status: <strong>' + point.status + '%</strong><br>';
      //   }
      // });
      // return s;
      for (var i = 0; i < this.series.length; i++) {
        // if (this.series[i].name == 'Average') {
        //console.log(this.series[i]);
        this.series[i].tooltip = {
          pointFormatter: function () {
            //let str = '<span style="color:' + this.series.color + ';">●</span> ' + this.series.name + ': <b>' + this.y + '</b><br>';
            let str = '<span class="date_of_reading" style="color:#444;">' + Highcharts.dateFormat('%A, %b %e %Y, %H:%M:%S', new Date(this.x)) + '</span><br>';
            str += '<span style="color:' + this.series.color + '; font-size:13px;">●</span> <span style="color:#444;font-size:13px;font-weight:bold">' + this.series.name + ': </span><span style="color:firebrick;">' + this.y + '</span> <img src="https://img.icons8.com/ios-filled/15/000000/vertical-line.png"/> ';

            if (this.series.name == 'Average' && this.status) {
              //str += '<span style="color:orange;">●</span> Status: <strong>' + this.status + '%</strong><br>';
              str += '<span style="color:orange; font-size:13px;">●</span> <span style="color:#444;font-size:13px;font-weight:bold">Status : </span><span style="color:firebrick;">' + this.status;
            }
            return str;
          }
        };
        // }
      }
    },


    setupCustomTooltip() {
      for (var i = 0; i < this.series.length; i++) {
        if (this.series[i].name == 'Average') {
          this.series[i].tooltip = {
            pointFormatter: function () {
              let str = '<span style="color:' + this.series.color + ';">●</span> ' + this.series.name + ': <b>' + this.y + '</b><br>';
              if (this.series.name == 'Average' && this.status) {
                str += '<span style="color:black;">●</span> Status: <strong>' + this.status + '%</strong><br>';
              }
              return str;
            }
          };
        }
      }
    },

    removeCustomTooltip() {
      for (var i = 0; i < this.series.length; i++) {
        if (this.series[i].tooltip && this.series[i].tooltip.pointFormatter) {
          delete this.series[i].tooltip.pointFormatter;
        }
      }
    },

    goToManage() {
      this.$router.push({
        name: 'soil_moisture_edit',
        params: { node_address: this.current_node }
      });
    },

    afterSetExtremes(e) {
      // initial load
      if (e.trigger == null) {
        if (this.bInitialQuery) {
          this.initial_start_date = this.xMin;
          this.initial_end_date = this.xMax;
          this.bInitialQuery = false;
        }
      } else {
        if (e.target.chart.resetZoomButton) {
          this.selection_start = e.min;
          this.selection_end = e.max;
        } else {
          this.selection_start = this.initial_start_date;
          this.selection_end = this.initial_end_date;
        }
        this.maybeReloadGraphData();
      }
    },

    // #3
    mapLoaded(e) {
      e.map._isVue = true;
      this.map = e.map;
      MapboxGL._isVue = true;
      this.mapboxObj = MapboxGL;
      this.mapActions = e.component.actions;
      this.loadFieldsData();
    },

    // #4
    loadFieldsData(is_refresh = false) {
      this.loading = true;
      this.$axios.post(this.$store.state.baseUrl + "/api/map_data", {
        is_refresh: is_refresh
      }).then(resp => {
        this.loading = false;
        if (
          resp.data &&
          resp.data.nodes &&
          resp.data.nodes.length
        ) {
          this.fieldsData = resp.data.nodes;
          this.populateMarkers();
          this.calcBounds();
          this.setupMap();
          //this.resizeTheMap();

          // bInitialQuery is for the first Ajax Query.
          if (this.bInitialQuery) {
            if (this.$store.state.mapSettingsCache.visited) {
              this.focusMarkersFly();
            } else {
              this.focusMarkersEase();
            }
            this.bInitialQuery = false;
            //this.refreshTimer = setInterval(() => { this.loadFieldsData(true); }, 60000*5);
          } else {
            // setInterval data refresh
            this.doTextSearch();
          }
        } else {

          swal.fire({
            title: 'No nodes found',
            text: 'Please add one or more nodes.',
            showCancelButton: false,
            confirmButtonText: 'OK',
            buttonsStyling: false,
            customClass: {
              cancelButton: 'btn btn-outline-primary',
              confirmButton: 'btn btn-primary'
            }
          });

        }
      }).catch(err => { this.loading = false; });
    },

    setupMap() {
      // handle resize when Argon menu collapses via click
      document.querySelector('.sidenav-toggler').addEventListener('click', this.resizeTheMap);
      // initial marker scale
      this.scaleMarkers();
    },

    // named event handler: https://stackoverflow.com/questions/28056716/check-if-an-element-has-event-listener-on-it-no-jquery/47337711
    resizeTheMap() {
      setTimeout(() => {
        this.map.resize();
      }, 500);
    },

    scaleMarkers() {
      this.markerScale = this.map.getZoom() / 22;
    },

    saveMapSettings() {
      let settings = {
        'visited': true,
        'zoom': this.map.getZoom(),
        'center': this.map.getCenter(),
        'infoFilterType': this.infoFilterType,
        'nodeFilterType': this.nodeFilterType
      };
      this.$store.dispatch('save_map_settings', settings);
    },

    focusMarkersEase() {
      this.mapActions.easeTo({
        center: this.bounds.getCenter(),
        duration: 2000,
        animate: true,
        easing: EasingFunctions.easeInOutQuad,
        essential: true
      }).then(() => {
        if (this.$store.state.mapSettingsCache.infoFilterType && this.$store.state.mapSettingsCache.nodeFilterType) {
          this.infoFilterType = this.$store.state.mapSettingsCache.infoFilterType;
          this.nodeFilterType = this.$store.state.mapSettingsCache.nodeFilterType;
        }
        this.$refs.focusmenu.expandMenu();
        this.filterFields();
      });
    },

    focusMarkersFly() {
      this.mapActions.flyTo({
        zoom: this.$store.state.mapSettingsCache.zoom,
        center: this.$store.state.mapSettingsCache.center,
        duration: 2000,
        animate: true,
        easing: EasingFunctions.easeInOutQuad,
        essential: true
      }).then(() => {
        if (this.$store.state.mapSettingsCache.infoFilterType && this.$store.state.mapSettingsCache.nodeFilterType) {
          this.infoFilterType = this.$store.state.mapSettingsCache.infoFilterType;
          this.nodeFilterType = this.$store.state.mapSettingsCache.nodeFilterType;
        }
        this.$refs.focusmenu.expandMenu();
        this.filterFields();
      });
    },

    toggleMarkerPopup(marker) {
      if (typeof this.visiblePopups[marker.node_address] === 'undefined' || this.visiblePopups[marker.node_address] === false) {
        this.visiblePopups[marker.node_address] = true;
        this.selectedNode = marker.node_address;
      } else if (this.visiblePopups[marker.node_address] === true) {
        this.visiblePopups[marker.node_address] = false;
        this.selectedNode = null;
        setTimeout(() => {
          this.$refs[marker.node_address][0].marker._popup.remove(); // emits @close event
        }, 200);
      }
    },

    popupClosed() {
      this.selectedNode = null;
    },

    filterNodeType() {
      // reset text search when node filter type is changed
      this.fieldNameSearch = '';
      this.filterLabels();
      this.filterFields();
      //console.log(this.map.getStyle().layers);
    },

    filterLabels() {
      this.map.getStyle().layers.forEach((layer, idx) => {
        if (layer.id.includes("-label") || layer.id.includes("-shield")) {
          if (this.infoFilterType.includes('labels')) {
            this.map.setLayoutProperty(
              layer.id,
              'visibility',
              'visible'
            );
          } else {
            this.map.setLayoutProperty(
              layer.id,
              'visibility',
              'none'
            );
          }
        }
      });
    },

    // mapbox @onzoomend && @ondragend event
    filterFields(e) {
      // Filter Markers
      if (this.fieldNameSearch == '') {
        let _bounds = this.map.getBounds();
        this.visibleMarkers = 0;
        this.markers.forEach((m, idx) => {
          this.markers[idx].visible = this.nodeFilterType.includes(m.node_type) && _bounds.contains(m.position) ? true : false;
          if (this.markers[idx].visible) { this.visibleMarkers++; }
        });
      }
      // save zoom and center
      this.saveMapSettings();
      // reset scrollbar
      document.querySelector('.focus_menu .items_section').scrollTop = 0;
    },

    populateMarkers() {
      if (this.fieldsData.length) {

        this.markers = [];
        this.fieldsData.forEach((item, index) => {
          // ensure non-zero coords
          if (item.lng && item.latt) {
            // create markers array

            this.markers.push({
              'id': item.id,
              'link': '/soil_moisture/graph/' + item.node_address,
              'nut_link': 'nutrients/graph/' + item.node_address,
              'status': item.status,
              'full': item.full,
              'refill': item.refill,
              'date_time': item.date_time,
              'date_diff': item.date_diff,
              'node_id': item.node_id,
              'node_address': item.node_address,
              'base_node_address': item.base_node_address,
              'field_id': item.field_id,
              'field_name': item.field_name,
              'graph_type': item.graph_type,
              'graph_start_date': item.graph_start_date,
              'node_type': this.formatNodeType(item.node_type),
              'position': [parseFloat(item.lng), parseFloat(item.latt)],
              'latt': parseFloat(item.lng),
              'lng': parseFloat(item.latt),
              'battLevel': item.bp,
              'pump_status': false,
              'visible': true,
              'sm1': item.sm1,
              'sm2': item.sm2,
              'sm3': item.sm3,
              'sm4': item.sm4,
              'sm5': item.sm5,
              'sm6': item.sm6,
              'sm7': item.sm7,
              'sm8': item.sm8,
              'sm9': item.sm9,
              'sm10': item.sm10,
              'sm11': item.sm11,
              'sm12': item.sm12,
              'sm13': item.sm13,
              'sm14': item.sm14,
              'sm15': item.sm15,
              't1': item.t1,
              't2': item.t2,
              't3': item.t3,
              't4': item.t4,
              't5': item.t5,
              't6': item.t6,
              't7': item.t7,
              't8': item.t8,
              't9': item.t9,
              't10': item.t10,
              't11': item.t11,
              't12': item.t12,
              't13': item.t13,
              't14': item.t14,
              't15': item.t15,
              'measurement_type': item.measurement_type,
              'last_reading': item.pulse_1 ? item.pulse_1 : '00000000',
              'charging': item.charging,
              'nutrient_lower': 'nutrient_lower' in item ? item.nutrient_lower : null,
              'nutrient_upper': 'nutrient_upper' in item ? item.nutrient_upper : null,
              'nutrient_gauge': 'nutrient_gauge' in item ? item.nutrient_gauge : null,
              'nutrient_avg': 'nutrient_avg' in item ? item.nutrient_avg : null,

              'marker_outline_color': item.marker_outline_color
            });
          }
        });
      }
      //console.log(this.markers);
    },

    calcMarkerStatus(status, refill, full) {
      //add the buffer for refill
      var ref = (refill * 30) / 100;
      refill = refill - ref;

      //add the buffer for full
      var ful = (full * 30) / 100;
      full = full + ful;

      //get the difference and calculate the percentage of status of the value between refill and full
      var diff = (full - refill);
      var x = (status / 100) * diff;
      status = x + refill;

      return status;

    },

    calcBufferRefill(refill) {
      var x = (refill * 30) / 100;
      return refill - x;
    },

    calcBufferFull(full) {
      var y = (full * 30) / 100;
      return full + y;
    },

    toggleBurger() {
      document.querySelector('.custom-nav-toggler').className = this.toggleVisible ? "custom-nav-toggler visible" : "custom-nav-toggler hidden";
      this.toggleVisible = !this.toggleVisible;
    },

    doTextSearch() {
      if (this.fieldNameSearch != '') {
        let resultCount = 0;
        this.markers.forEach((m, idx) => {
          // Search by field name OR
          // Search by Node Address
          // Should Respect Node Filter
          if ((this.nodeFilterType.includes(m.node_type)) && (
            (m.field_name.toLowerCase().indexOf(this.fieldNameSearch.toLowerCase()) > -1) ||
            (m.node_address.toLowerCase().indexOf(this.fieldNameSearch.toLowerCase()) > -1)
          )
          ) {
            this.markers[idx].visible = true;
            resultCount++;
          } else {
            this.markers[idx].visible = false;
          }
        });
        if (resultCount) {
          if (this.markers.length) {
            this.calcBounds();
            this.map.fitBounds(this.bounds, {
              linear: true,
              maxZoom: 13,
              duration: 2000,
              animate: true,
              padding: 100,
              easing: EasingFunctions.easeInOutQuad,
              essential: true
            });
          }
        }
      } else {
        this.showAllMarkers();
        this.filterFields();
      }
    },

    focusMarker(marker) {
      if (marker.node_address == this.selectedNode) {
        this.selectedNode = null;
      } else {
        this.selectedNode = marker.node_address;
      }
      // pan to marker
      this.mapActions.flyTo({
        center: marker.position,
        duration: 1000,
        animate: true,
        easing: EasingFunctions.easeInOutQuad,
        essential: true
      }).then(() => {
        this.$refs[marker.node_address][0].togglePopup();
      });
    },

    showAllMarkers() {
      this.markers.forEach((m, idx) => {
        this.markers[idx].visible = true;
      });
      this.visibleMarkers = this.markers.length;
    },

    calcBounds() {
      let _bounds = new this.mapboxObj.LngLatBounds();

      // Limit Bounds to United States
      let us_t = 49.3457868;   // north lat
      let us_b = 24.7433195;   // south lat
      let us_l = -124.7844079; // west long
      let us_r = -66.9513812;  // east long

      this.markers.forEach((m, idx) => {
        if (this.markers[idx].visible) {
          if (this.markers[idx].position[0] > us_l && this.markers[idx].position[0] < us_r &&
            this.markers[idx].position[1] > us_b && this.markers[idx].position[1] < us_t) {
            _bounds.extend(this.markers[idx].position);
          }
          //_bounds.extend(this.markers[idx].position);
        }
      });
      this.bounds = _bounds;
    },

    /* TODO: Add to Utils Mixin */
    calcMarkerIconColor(marker, reading, type) {
      if (type == 'sm') {
        let color = 'white';
        if (reading >= 0 && reading < 25) { color = "red"; }
        else if (reading >= 25 && reading < 50) { color = "orange"; }
        else if (reading >= 50 && reading < 75) { color = "green"; }
        else if (reading >= 75 && reading <= 100) { color = "blue"; }
        else { color = "red"; }
        if (marker.date_diff.days >= 3) { color = "yellow"; }
        return color;
      } else if (type == 'well') {
        return "red";
      } else if (type == 'meter') {
        return "blue";
      } else {
        return "black";
      }
    },

    calcMarkerBorderColor(marker) {
      return "border: 2px solid " + marker.marker_outline_color;
    },

    formatNodeType(type) {
      if (type == 'Soil Moisture') return 'sm';
      if (type == 'Nutrients') return 'nut';
      if (type == 'Wells') return 'well';
      if (type == 'Water Meter') return 'meter';
      return 'sm';
    },

    formatUOM(value) {
      if (value == 1) { return 'cm3'; }
      if (value == 2) { return 'gal'; }
      return '';
    },

    togglePump(marker) {
      this.loading = true;
      if (marker.node_address) {
        this.$axios.get(this.$store.state.baseUrl + "/api/toggleWM/" + marker.node_address)
          .then((resp) => {
            this.loading = false;
            this.$notify({
              title: 'Status',
              message: 'Pump Toggled',
              type: 'info',
              verticalAlign: 'top',
              horizontalAlign: 'right'
            });
          });
      }
    },

    toggleLegendModal() {
      this.showLegendModal = !this.showLegendModal;
    },

    renderSvgIcon(type) {
      if (type == 'well') {
        return '<svg stroke-linejoin="bevel" fill-rule="evenodd" xmlns="http://www.w3.org/2000/svg" overflow="visible" width="100%" height="100%" viewBox="0 0 165 187.5"><path stroke-width="0" d="M.001 187.5v-7.499h7.484v-52.499h7.482v-7.501h7.482V67.5H7.484v-7.499h14.965V39.002L7.484 45.001H0V30h7.484v.001l74.829-30 74.83 30h7.483v15.001h-7.484l-.001-.001-14.964-5.998.001 20.998h7.857v-.002h7.482V75H165v7.499h-7.483v.001h-7.482v-15h-7.857v52.501h7.482v7.501h7.483v52.499h7.482v7.499H.001zm119.728-59.998v-7.501h7.482V67.5h-14.967v7.501h-26.04v15h18.558l-7.484 30h-29.93l-7.483-30h18.857v-15h-26.34V67.5H37.416v52.501h7.482v7.501h74.831zM52.382 60.001v-7.499h59.862v7.499h14.967V33.002L82.313 15.001l-44.897 18v27h14.966z" fill="currentColor" stroke="white" font-family="Times New Roman" font-size="16"/></svg>';
      }
      if (type == 'sm') {
        // return '<svg stroke-linejoin="bevel" fill-rule="evenodd" xmlns="http://www.w3.org/2000/svg" overflow="visible" width="100%" height="100%" viewBox="0 0 90 187.5"><path stroke-width="0" d="M37.499 187.5l-15-15V45l-7.5-7.5V7.499H0V0h74.999v7.499H59.998v7.502h22.5v-7.5h7.501V37.5h-7.501V30h-22.5l.001 7.5-7.5 7.5v127.5l-15 15z" fill="currentColor" stroke="white" font-family="Times New Roman" font-size="16"/></svg>';
        return '<svg stroke-width=".501" stroke-linejoin="bevel" fill-rule="evenodd" xmlns="http://www.w3.org/2000/svg" overflow="visible" width="100%" height="100%" viewBox="0 0 90 187.5"><g stroke-linecap="round" stroke="none" fill="none" font-size="16"><path d="m37.499 187.5-15-15V45l-7.5-7.5V7.499H0V0h74.999v7.499H59.998v7.502h22.5v-7.5h7.501V37.5h-7.501V30h-22.5l.001 7.5-7.5 7.5v127.5l-15 15Z" fill="currentColor"/><path d="m7.177 124.653 15.222-1.48c.916 5.109 2.776 8.861 5.576 11.258 2.801 2.396 6.579 3.595 11.338 3.595 5.038 0 8.835-1.066 11.39-3.198 2.554-2.132 3.83-4.625 3.832-7.48-.002-1.831-.538-3.39-1.613-4.677-1.074-1.285-2.95-2.404-5.629-3.355-1.831-.635-6.008-1.762-12.527-3.383-8.386-2.079-14.27-4.635-17.653-7.665-4.757-4.263-7.135-9.461-7.135-15.592 0-3.946 1.119-7.637 3.356-11.072 2.237-3.437 5.462-6.053 9.673-7.85 4.211-1.796 9.294-2.695 15.247-2.695 9.726 0 17.046 2.132 21.962 6.395 4.915 4.264 7.496 9.954 7.743 17.072l-15.645.687c-.669-3.981-2.104-6.844-4.307-8.588-2.202-1.744-5.506-2.617-9.911-2.617-4.544 0-8.104.935-10.677 2.802-1.657 1.198-2.483 2.801-2.483 4.81 0 1.832.774 3.4 2.325 4.704 1.973 1.655 6.766 3.381 14.376 5.179 7.612 1.797 13.241 3.656 16.888 5.576 3.647 1.922 6.501 4.547 8.562 7.875 2.061 3.33 3.092 7.444 3.092 12.342 0 4.439-1.232 8.598-3.699 12.474-2.466 3.877-5.956 6.757-10.466 8.641-4.51 1.885-10.13 2.828-16.861 2.828-9.796 0-17.319-2.264-22.569-6.792-5.25-4.527-8.386-11.126-9.407-19.794Z" fill-rule="nonzero" stroke-linejoin="round" stroke-width=".375" fill="#fff" stroke-miterlimit="79.84"/></g></svg>';
      }
      if (type == 'nut') {
        // return '<svg stroke-linejoin="bevel" fill-rule="evenodd" xmlns="http://www.w3.org/2000/svg" overflow="visible" width="100%" height="100%" viewBox="0 0 90 187.5"><path stroke-width="0" d="M37.499 187.5l-15-15V45l-7.5-7.5V7.499H0V0h74.999v7.499H59.998v7.502h22.5v-7.5h7.501V37.5h-7.501V30h-22.5l.001 7.5-7.5 7.5v127.5l-15 15z" fill="currentColor" stroke="white" font-family="Times New Roman" font-size="16"/></svg>';
        return '<svg stroke-width=".501" stroke-linejoin="bevel" fill-rule="evenodd" xmlns="http://www.w3.org/2000/svg" overflow="visible" width="100%" height="100%" viewBox="0 0 90 187.5"><g stroke-linecap="round" stroke="none" fill="none" font-size="16"><path d="m37.499 187.5-15-15V45l-7.5-7.5V7.499H0V0h74.999v7.499H59.998v7.502h22.5v-7.5h7.501V37.5h-7.501V30h-22.5l.001 7.5-7.5 7.5v127.5l-15 15Z" fill="currentColor"/><path d="M9.001 147.09V75.155h14.132l29.441 48.039V75.155h13.493v71.935H51.494l-28.999-46.909v46.909H9.001Z" fill-rule="nonzero" stroke-linejoin="round" stroke-width=".375" fill="#fff" stroke-miterlimit="79.84"/></g></svg>';
      }
      if (type == 'meter') {
        return '<svg stroke-linejoin="bevel" fill-rule="evenodd" xmlns="http://www.w3.org/2000/svg" overflow="visible" width="100%" height="100%" viewBox="0 0 225 150"><path stroke-width="0" d="M43.746 105.001H22.499V120H0V29.999h22.499V45h21.246C55.326 18.519 81.762 0 112.498 0c30.735 0 57.171 18.519 68.752 45h21.249V29.999h22.499V120h-22.499v-14.999h-21.25C169.669 131.482 143.233 150 112.498 150c-30.736 0-57.172-18.518-68.752-44.999zM179.998 75c0-37.26-30.24-67.5-67.5-67.5-37.26 0-67.5 30.24-67.5 67.5 0 37.26 30.24 67.5 67.5 67.5 37.26 0 67.5-30.24 67.5-67.5zm-127.5 0c0-33.12 26.88-60 60-60 33.12 0 60 26.88 60 60 0 33.12-26.88 60-60 60-33.12 0-60-26.88-60-60zm37.5 22.5V82.501H74.999V97.5h14.999zm30-.001V82.5h-14.999v14.999h14.999zm30 0V82.5h-14.999v14.999h14.999zm.001-30V52.5H74.998v14.999h75.001z" fill="currentColor" stroke="white" font-family="Times New Roman" font-size="16"/></svg>';
      }
    },

    goToGraph(marker) {
      if (this.userCan('Graph', 'Soil Moisture', marker.node_id, 'O')) {
        this.$router.push({
          name: "soil_moisture_graph",
          params: { node_address: marker.node_address }
        });
      }
    }
  },

  beforeRouteLeave(to, from, next) {
    // if(this.refreshTimer){
    //   console.log("Clearing Map Timer");
    //   clearInterval(this.refreshTimer);
    // }
    next();
  }
};

</script>
<style scoped>
.mapboxgl-canvas {
  width: 100%;
  height: auto;
}

.map_container {
  position: relative;
  width: 100%;
  height: 100vh;
}

/* Field Marker Styling */

.field_marker:hover {
  cursor: pointer;
}

/* Focus menu */

/* Focus Menu - Search Field */

.fields_search {
  padding: 0.5rem;
  height: 2rem;
  /*margin: 0.5rem;*/
  width: calc(100% - 1rem);
}

.fields_search::placeholder,
.fields_search::placeholder-shown {
  color: #1CA554;
}

/* Focus Menu - Search Field - Clear Button */

.fields_search_clear_btn {
  position: absolute;
  width: 1rem;
  height: 1rem;
  border-radius: 0.1rem;
  right: 0.5rem;
  /* was 1.0 rem */
  top: 50%;
  transform: translateY(-50%);
  text-align: center;
  font-size: 1rem;
  line-height: 1;
  background-color: #00A04C;
  color: white;
  cursor: pointer;
}

.fields_search_clear_btn:after {
  content: '+';
  display: block;
  font-weight: bold;
  transform: rotate(45deg);
}

/* Focus Menu - Field Records */

.field_record {
  position: relative;
  padding: 0.5rem;
  padding-right: 4rem;
  margin-right: 0.6rem;
  margin-top: 1px;
  min-height: 6rem;
  font-size: 0.8rem;
  line-height: 1.2;
  color: black;
  border-bottom: 1px solid #eee;
}

.field_record:hover {
  cursor: pointer;
}

.field_record .field_name {
  font-weight: bold;
}

.field_record .indicators {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 3.5rem;
  margin: 0 0.5rem;
  display: flex;
  flex-flow: row;
  align-items: center;
  justify-content: flex-end;
}

.field_record .indicators>div {
  display: block;
  margin: 0 0.25rem;
}

.field_record_selected {
  /*background: linear-gradient(90deg, rgba(255,255,255,0.5), transparent);*/
  outline: 1px solid #01A04D;
  margin-left: 0.6rem;
  transition: all .25s ease-in-out;
}

.ps__rail-y {
  z-index: 99;
}

/* Top right Filter Menu */

.info_filter_menu {
  position: absolute;
  display: block;
  top: 1rem;
  right: 1rem;
  z-index: 99;
}

.info_filter_menu .btn:hover {
  cursor: pointer;
}

/* Side Menu Node Type Filter Menu */
.type_filter_menu {
  width: 100%;
  padding: 0 0.5rem;
}

/* Selected Node */

.selected_node {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.25em;
}

/* Dialog Styling */

.marker_info_wrap {
  display: flex;
  justify-content: center;
  flex-flow: column;
  align-items: center;
  font-size: 0.9rem;
}

.marker_info_wrap .field_name {
  padding: 0.25rem 0.5rem;
  color: white;
  font-weight: bold;
  font-size: 1.1rem;
  font-family: 'Open Sans', sans-serif;
  text-shadow: 0 0 5px black;
  text-align: center;
}

.marker_info_wrap .node_address,
.marker_info_wrap .last_reading_date {
  display: block;
  /*font-family: 'Roboto Mono', monospace;*/
  font-family: 'Open Sans', sans-serif;
  padding: 0 0.25rem;
  color: white;
  width: 100%;
  text-align: center;
}

.marker_info_wrap .pump_control .pump_control_inner {
  display: flex;
  flex-flow: column;
  width: 100%;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  color: white;
  box-shadow: inset 0 0 10px #000;
  background: #222;
  border-radius: 0.25rem;
  border: 1px solid #333;
}

.marker_info_wrap .meter_control {
  display: flex;
  flex-flow: column;
  width: 100%;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
}

.marker_info_wrap .sm_control {
  display: flex;
  flex-flow: row wrap;
  /* NEW */
  justify-content: center;
  align-items: flex-start;
  /* NEW */
  /*margin:0.25rem 0;*/
  margin: 0;
}

.marker_info_wrap .sm_control .sm_control_item {
  display: flex;
  flex-flow: column;
  align-items: center;
  flex: 1 0 33.3333%;
  /* NEW */
}

@media(max-width:1023px) {
  .marker_info_wrap .sm_control .sm_control_item {
    flex: 1 0 50%;
    /* NEW */
  }
}

.marker_info_wrap .sm_control .sm_control_item .marker_info_horz {
  display: flex;
  flex-flow: row;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  margin: 0.75rem 0;
}

.marker_info_wrap .sm_control .sm_control_item .marker_info_horz>* {
  margin: 0 0.25rem;
}

.search_wrapper {
  position: relative;
  margin: 0.5rem;
}

.clearfix::after {
  content: "";
  clear: both;
  display: table;
}

.legend_btn {
  position: absolute;
  bottom: 2rem;
  right: 0.5rem;
}

.legend_btn .circle {
  display: inline-block;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 1.25rem;
  background: #eee;
  color: #111;
  font-weight: bold;
}

.marker {
  width: 50px;
  height: 50px;
  border: 2px solid white;
  padding: 0.4em;
  border-radius: 100%;
}

.marker:hover {
  cursor: pointer;
}

.legend_modal .legend_box .licon {
  width: 1.5rem;
  height: 1.5rem;
  margin-right: 0.5rem;
}

.legend_modal .legend_box .blue {
  color: blue;
}

.legend_modal .legend_box .red {
  color: red;
}

.legend_modal .legend_box .yellow {
  color: yellow;
}

.legend_modal .legend_box .orange {
  color: orange;
}

.legend_modal .legend_box .green {
  color: green;
}
</style>

